import { createContext, use, useEffect, useState, type ReactNode } from 'react'

export type Theme = 'system' | 'light' | 'dark'

const STORAGE_KEY = 'ai-sales:theme'

/**
 * Выбор оформления.
 *
 * Это единственное место в интерфейсе, где используется хранилище браузера,
 * и причина узкая: выбор темы — удобство одного человека в одном браузере,
 * его незачем держать на сервере. Всё остальное состояние живёт в адресе
 * или на сервере, чтобы ссылка на экран оставалась ссылкой.
 *
 * Обращения к localStorage обёрнуты в try/catch: в приватном окне и при
 * запрещённых данных сайта он не читается и не пишется, а падать из-за
 * настройки темы интерфейс не должен.
 */
interface ThemeState {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeState | null>(null)

function readStored(): Theme {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'system'
  } catch {
    return 'system'
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStored)

  useEffect(() => {
    const root = document.documentElement

    /**
     * «Как в системе» — это отсутствие атрибута, а не его значение.
     * Тогда работает медиа-запрос prefers-color-scheme, и тема меняется
     * вслед за системной без перезагрузки страницы.
     */
    if (theme === 'system') {
      root.removeAttribute('data-theme')
    } else {
      root.setAttribute('data-theme', theme)
    }

    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      // Приватное окно или запрещённые данные сайта — тема просто не запомнится
    }
  }, [theme])

  return <ThemeContext value={{ theme, setTheme: setThemeState }}>{children}</ThemeContext>
}

export function useTheme(): ThemeState {
  const state = use(ThemeContext)

  if (!state) {
    throw new Error('useTheme вызван вне ThemeProvider')
  }

  return state
}
